import { useState } from 'react'

import { useAuth } from '~/auth/auth-context'
import { FoodMark } from '~/components/food-mark'
import { ChevronDownIcon, CloseIcon, DeleteIcon, PlusIcon } from '~/components/icons'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover'
import {
  MEASURES,
  amountSaid,
  macroLine,
  slotOff,
  standingOn,
  takesAWeight,
  unitsOf,
  type Amount,
  type Food,
  type MealAlternative,
  type MealSlot,
  type MealState,
} from '~/data/nutrition'

/** Where one alternative stands: its slot, and itself. */
export interface SlotAt {
  readonly slotId: string
  readonly altId: string
}

/**
 * The meal's slots, each drawn as the alternative it stands on — the nutrition spec, Meals.
 * A slot that is off is drawn faint, saying so. How much of an alternative is typed on its own
 * card, as it is on the phone — the number over the unit it is counted in, and the unit is
 * changed where it stands — and at the card's end one control opens what the slot can stand
 * on: each alternative with the one it stands on marked, off, and adding an alternative;
 * beside it, the way to take the alternative shown off the meal.
 */
export function MealSlotList({
  slots,
  state,
  onTurn,
  onAdjust,
  onAmount,
  onAmountStart,
  onAmountDone,
  onAddTo,
  onRemove,
  disabled,
  estimated = false,
  plain = false,
}: {
  slots: readonly MealSlot[]
  state: MealState
  /**
   * A plain list: what is on it, in amounts, and nothing else — the nutrition spec, Sending
   * food. Alternatives and turning something off are both about a meal that is kept and logged
   * again, so a plate put together for somebody else offers neither.
   */
  plain?: boolean
  /** Turning a slot to an alternative, or — null — off. */
  onTurn: (slotId: string, altId: string | null) => void
  /** Opening an alternative: a food on its own page, a quick entry for its figures. */
  onAdjust: (at: SlotAt) => void
  /** How much of an alternative, typed on its card, or counted in another unit. */
  onAmount: (at: SlotAt, amount: Amount) => void
  /** The amount taken hold of and let go of, so a run of typing is one step back. */
  onAmountStart: () => void
  onAmountDone: () => void
  /** Adding another alternative to a slot. */
  onAddTo: (slotId: string) => void
  /** Taking the alternative shown off the meal for good. */
  onRemove: (at: SlotAt) => void
  /** Whether the rows take anything at all — an answer takes nothing while it is being asked. */
  disabled?: boolean | undefined
  /** Whether the rows are an estimate the AI made, whose amounts are what is being checked. */
  estimated?: boolean | undefined
}) {
  return (
    <ul className="space-y-1.5" aria-label="Members">
      {slots.map((slot) => {
        const shown = standingOn(slot, state)
        if (shown === null) {
          return null
        }
        const off = slotOff(state, slot)
        const member = shown.member
        const at = { slotId: slot.id, altId: shown.id }
        /*
         * A quick entry counts as one amount of itself, and saying so says nothing — unless
         * it is an estimate the AI made, whose amount is the whole of what is being checked.
         */
        const said = member.food !== null || estimated ? amountSaid(member.amount) : null
        return (
          <li
            key={slot.id}
            className={`flex items-center gap-2 rounded-2xl py-1 pr-1 ring-1 ring-white/10 transition-colors hover:bg-hover ${off ? 'opacity-50' : ''}`}
          >
            <button
              type="button"
              aria-label={[
                `Change ${member.name}`,
                said,
                off ? 'left out this time' : macroLine(member.nutrients),
              ]
                .filter((part) => part !== null)
                .join(', ')}
              disabled={disabled === true || off}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl px-3 py-2.5 text-left"
              onClick={() => {
                onAdjust(at)
              }}
            >
              {member.food === null ? (
                <FoodMark name={member.name} />
              ) : (
                <FoodMark food={member.food} />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate">{member.name}</span>
                <span className="block truncate text-sm text-ink-muted">
                  {off ? 'Not this time' : macroLine(member.nutrients)}
                </span>
              </span>
            </button>
            {member.food !== null && !off && (
              <AmountBox
                food={member.food}
                name={member.name}
                amount={member.amount}
                disabled={disabled === true}
                onAmount={(next) => {
                  onAmount(at, next)
                }}
                onStart={onAmountStart}
                onDone={onAmountDone}
              />
            )}
            {!plain && (
              <SlotControl
                slot={slot}
                shown={shown}
                off={off}
                disabled={disabled === true}
                onTurn={(altId) => {
                  onTurn(slot.id, altId)
                }}
                onAdd={() => {
                  onAddTo(slot.id)
                }}
              />
            )}
            {/* Taking the alternative shown off the meal, on the card itself. */}
            <button
              type="button"
              aria-label={`Remove ${member.name}`}
              disabled={disabled === true}
              className="shrink-0 rounded-full p-1.5 text-ink-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-40"
              onClick={() => {
                onRemove(at)
              }}
            >
              <DeleteIcon aria-hidden className="size-5" />
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * How much of an alternative, on its card: the number over the unit it is counted in, as the
 * phone draws it. Pressing the unit opens the units the food can be counted in — its own
 * weight or volume, and each of its portions — and choosing one keeps the number.
 */
function AmountBox({
  food,
  name,
  amount,
  disabled,
  onAmount,
  onStart,
  onDone,
}: {
  food: Food
  name: string
  amount: Amount
  disabled: boolean
  onAmount: (next: Amount) => void
  onStart: () => void
  onDone: () => void
}) {
  const { units: accountUnits } = useAuth()
  const [open, setOpen] = useState(false)
  const units: { key: string; label: string; amount: Amount }[] = [
    ...(takesAWeight(food)
      ? unitsOf(food, accountUnits).map((unit) => ({
          key: unit,
          label: `${unit} · ${MEASURES[unit].measure}`,
          amount: { value: amount.value, unit, portion: null },
        }))
      : []),
    ...food.portions.map((portion) => ({
      key: `portion:${portion.name}`,
      label: portion.name,
      amount: { value: amount.value, unit: 'portion' as const, portion: portion.name },
    })),
  ]
  const chosenKey = amount.unit === 'portion' ? `portion:${amount.portion ?? ''}` : amount.unit
  const unitSaid = amount.unit === 'portion' ? (amount.portion ?? 'portion') : amount.unit
  return (
    <div className="flex w-24 shrink-0 flex-col overflow-hidden rounded-xl bg-surface ring-1 ring-white/10">
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        aria-label={`Amount of ${name}`}
        disabled={disabled}
        value={amount.value === 0 ? '' : String(Math.round(amount.value * 100) / 100)}
        onFocus={(event) => {
          onStart()
          event.target.select()
        }}
        onChange={(event) => {
          const read = Number(event.target.value)
          onAmount({
            ...amount,
            value: event.target.value.trim() === '' || !Number.isFinite(read) ? 0 : read,
          })
        }}
        onBlur={onDone}
        className="w-full bg-transparent px-2 pt-1.5 pb-0.5 text-center text-base tabular-nums outline-none [appearance:textfield] focus:bg-hover [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Unit of ${name}`}
            disabled={disabled}
            className="w-full truncate border-t border-white/10 px-2 pt-0.5 pb-1 text-xs text-ink-muted transition-colors hover:bg-hover hover:text-ink"
          >
            {unitSaid}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-52 p-2">
          <ul role="radiogroup" aria-label="Unit" className="space-y-0.5">
            {units.map((unit) => (
              <li key={unit.key}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={unit.key === chosenKey}
                  className={`w-full rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-hover ${
                    unit.key === chosenKey ? 'bg-accent/10 text-accent' : ''
                  }`}
                  onClick={() => {
                    onStart()
                    onAmount(unit.amount)
                    onDone()
                    setOpen(false)
                  }}
                >
                  {unit.label}
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  )
}

/**
 * The control that opens what a slot can stand on: each alternative with the chosen one
 * marked, then not this time, then adding an alternative. Each wears its mark — the food's
 * face, a cross, a plus — so the list is read at a glance. Choosing puts it away.
 */
function SlotControl({
  slot,
  shown,
  off,
  disabled,
  onTurn,
  onAdd,
}: {
  slot: MealSlot
  shown: MealAlternative
  off: boolean
  disabled: boolean
  onTurn: (altId: string | null) => void
  onAdd: () => void
}) {
  const [open, setOpen] = useState(false)
  const row =
    'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-hover'
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Turn ${shown.member.name}, one of ${String(slot.alternatives.length)}`}
          disabled={disabled}
          className="shrink-0 rounded-full p-1.5 text-ink-muted transition-colors hover:bg-hover-strong hover:text-ink disabled:opacity-40 data-[state=open]:bg-hover-strong"
        >
          <ChevronDownIcon
            aria-hidden
            className={`size-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <ul className="space-y-0.5" aria-label="Slot">
          {slot.alternatives.map((alt) => (
            <li key={alt.id}>
              <button
                type="button"
                aria-pressed={!off && alt.id === shown.id}
                className={`${row} ${!off && alt.id === shown.id ? 'bg-accent/10 text-accent' : ''}`}
                onClick={() => {
                  onTurn(alt.id)
                  setOpen(false)
                }}
              >
                {alt.member.food === null ? (
                  <FoodMark name={alt.member.name} size="size-8 text-base" />
                ) : (
                  <FoodMark food={alt.member.food} size="size-8 text-base" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{alt.member.name}</span>
                  <span className="block truncate text-xs text-ink-muted">
                    {macroLine(alt.member.nutrients)}
                  </span>
                </span>
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              aria-pressed={off}
              className={`${row} ${off ? 'bg-accent/10 text-accent' : ''}`}
              onClick={() => {
                onTurn(null)
                setOpen(false)
              }}
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-surface ring-1 ring-white/10">
                <CloseIcon aria-hidden className="size-4" />
              </span>
              <span className="truncate text-sm">Not this time</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              className={`${row} text-accent`}
              onClick={() => {
                setOpen(false)
                onAdd()
              }}
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-surface ring-1 ring-white/10">
                <PlusIcon aria-hidden className="size-4" />
              </span>
              <span className="truncate text-sm">Add an alternative</span>
            </button>
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  )
}
